'use client';

/**
 * ObeyaCostPlanRoomsCard — room-allocation matrix: lines down the rows,
 * every room-programme slot across the columns, quantity per cell.
 *
 * Not a fourth group-by mode on ObeyaCostPlanLinesCard. That card's
 * Line/Slot/Vendor grouping is a list of groups with expandable leaf
 * rows over one flat pivot source — the right shape for browsing and
 * summing. A true row×column crosstab, with drag-select-a-range-then-
 * type-once-to-fill, is a different visual concept a grouped list
 * can't express — the bulk-editing shape a one-line-at-a-time picker
 * (see the Line-mode "+ Assign to a room" control) genuinely doesn't
 * scale past a handful of allocations.
 *
 * Self-fetches costplan_details (line_items + room_placements) and
 * mcp_room_programme (the column roster) — the same two strategies
 * ObeyaCostPlanLinesCard and the retired concertina room board already
 * read. No new backend read path. Cell writes go through
 * mcp_line_edit's room_placement upsert, one atomic call per cell — a
 * range fill is client-side sequencing of that same single-cell write,
 * not a new bulk-write endpoint. Sequential, not parallel: two cells in
 * the same row (same line, different slots) must not race each other's
 * append-only ValueState writes.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaCostPlanRoomsCard
 * @see klyde_edge/klyde_intelligence/projects/mcp.py — edit_line's room_placement param
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo, useRef, useState } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useStrategyData, useMcpLineEdit } from '@/lib/api/hooks';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';

interface RoomPlacement { slot_sid: string; slot_name: string; slot_count: number; qty: number; }
interface LineItem {
  line_id: string | null; product_sid: string | null; product_name: string | null;
  product_sku: string | null; vendor_id: string | null; vendor_name: string | null; quantity: number;
  image_url: string | null; room_placements: RoomPlacement[];
  // The schedule swimlane this candidate was picked to satisfy, when it
  // came from Discovery — label resolved live server-side, never a
  // snapshot. Null for lines added by plain catalogue browsing.
  source_line_sid: string | null; source_line_label: string | null;
  // Room-programme slot(s) this line's source schedule item plausibly
  // belongs to — resolved server-side (see costplan_line_items_from_
  // graph_rows). Empty when there's no source line or nothing matched;
  // never a client-side guess.
  candidate_slot_sids: string[];
}
interface CostplanDetailsData { line_items: LineItem[]; labels?: Record<string, string> }
interface RoomSlot {
  sid: string; name: string; count: number | null; kind: string;
  // Split a repeating unit type into functional zones (Living/Bedroom/
  // Balcony) — optional, absent for slots with no internal subdivision
  // (e.g. individually numbered rooms, a shared amenity).
  sub_area?: number | null; sub_area_label?: string | null;
}
interface RoomProgrammeData { room_types: RoomSlot[]; specific_rooms: RoomSlot[] }

interface CellSel { rowStart: number; rowEnd: number; colStart: number; colEnd: number }

export function ObeyaCostPlanRoomsCard() {
  const { mcpId, projectId } = useViewportSelection();
  // Same mechanism costplan-lines-card.tsx uses to drive the right
  // rail — a Line row's product wasn't wired to it here at all, so
  // clicking a line in the Rooms grid never updated Insights/Copilot.
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  // Same "activated" convention costplan-lines-card.tsx uses for its
  // clickable line name — text-primary + underline, not just a row
  // background tint — so a selection reads consistently across both
  // cost-plan views.
  const selectedId = useGraphSelectionStore((s) => s.selectedNode?.id);
  const selectLine = (line: LineItem) => {
    if (!line.product_sid) return;
    const relatedVendor = line.vendor_id
      ? { id: line.vendor_id, label: line.vendor_name ?? 'Vendor', type: 'vendor', properties: {} }
      : null;
    selectNode(
      { id: line.product_sid, label: line.product_name ?? 'Product', type: 'product', properties: {} },
      undefined, relatedVendor,
    );
  };
  const { contentKey } = useCardIdentity();
  const detailsStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'detail'));
  const roomStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'room_programme'));
  const editStrategy = useCapabilityStore((s) => s.strategyName('cost_plans', 'line_edit'));
  const editLine = useMcpLineEdit(editStrategy);

  const { data: details, isLoading } = useStrategyData<CostplanDetailsData>(
    detailsStrategy, { semantic_id: mcpId ?? '' }, !!mcpId && !!detailsStrategy,
  );
  const { data: roomData } = useStrategyData<RoomProgrammeData>(
    roomStrategy, { project_sid: projectId ?? '' }, !!projectId && !!roomStrategy,
  );

  const lines = details?.data?.line_items ?? [];
  const slots: RoomSlot[] = useMemo(() => [
    ...(roomData?.data?.room_types ?? []),
    ...(roomData?.data?.specific_rooms ?? []),
  ], [roomData]);

  // Two-row header groups consecutive slots sharing an area name (e.g.
  // 4 "Co-Living/Studio" sub-areas) under one spanning cell instead of
  // repeating the area name on every column — presentation only, the
  // columns underneath are still one per sub-area slot, unchanged.
  const areaGroups = useMemo(() => {
    const groups: { name: string; count: number }[] = [];
    slots.forEach((s) => {
      const last = groups[groups.length - 1];
      if (last && last.name === s.name) last.count += 1;
      else groups.push({ name: s.name, count: 1 });
    });
    return groups;
  }, [slots]);

  const placementMap = useMemo(() => {
    const m = new Map<string, number>();
    lines.forEach((l) => (l.room_placements ?? []).forEach((p) => m.set(`${l.line_id}::${p.slot_sid}`, p.qty)));
    return m;
  }, [lines]);

  // Cells render two loops deep — can't call useAffordance() per cell at
  // this cardinality (lines × slots). One subscription, plain string
  // interpolation per cell, same pattern agent-canvas-overlay.tsx's
  // removeLineTestId already uses at smaller scale.
  const affordances = useTotalMatrixStore((s) => s.affordances);
  const cellTestId = (cellId: string): string | undefined => {
    const entry = affordances?.['canvas.costplan_rooms.cell'];
    return entry ? entry.test_id.replace('{cell_id}', cellId) : undefined;
  };
  const fillApplyTestId = affordances?.['canvas.costplan_rooms.fill_apply']?.test_id;

  const [sel, setSel] = useState<CellSel | null>(null);
  const [dragging, setDragging] = useState(false);
  const [fillValue, setFillValue] = useState('');
  const [applying, setApplying] = useState(false);
  const anchorRef = useRef<{ row: number; col: number } | null>(null);

  const inSel = (r: number, c: number) =>
    !!sel && r >= sel.rowStart && r <= sel.rowEnd && c >= sel.colStart && c <= sel.colEnd;

  const startSel = (r: number, c: number) => {
    anchorRef.current = { row: r, col: c };
    setSel({ rowStart: r, rowEnd: r, colStart: c, colEnd: c });
    setDragging(true);
  };
  const extendSel = (r: number, c: number) => {
    if (!dragging || !anchorRef.current) return;
    const a = anchorRef.current;
    setSel({
      rowStart: Math.min(a.row, r), rowEnd: Math.max(a.row, r),
      colStart: Math.min(a.col, c), colEnd: Math.max(a.col, c),
    });
  };
  const endDrag = () => setDragging(false);

  const selCount = sel ? (sel.rowEnd - sel.rowStart + 1) * (sel.colEnd - sel.colStart + 1) : 0;

  const applyFill = async (value: number) => {
    if (!sel || !mcpId) return;
    setApplying(true);
    // Sequential, not Promise.all — two cells in the same row target the
    // same line's append-only ValueState; concurrent writes could land
    // out of order and one would silently clobber the other's slot.
    for (let r = sel.rowStart; r <= sel.rowEnd; r++) {
      const line = lines[r];
      if (!line?.line_id) continue;
      for (let c = sel.colStart; c <= sel.colEnd; c++) {
        const slot = slots[c];
        if (!slot?.sid) continue;
        const current = placementMap.get(`${line.line_id}::${slot.sid}`) ?? 0;
        if (current === value) continue;
        // eslint-disable-next-line no-await-in-loop
        await editLine.mutateAsync({ semantic_id: mcpId, line_id: line.line_id, room_placement: { slot_sid: slot.sid, qty: value } });
      }
    }
    setApplying(false);
    setSel(null);
    setFillValue('');
  };

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_costplan_rooms',
    status: (details ? 'loaded' : mcpId && detailsStrategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: null,
    text: `Room matrix: ${lines.length} lines × ${slots.length} slots`,
    json: null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, details, mcpId, detailsStrategy, lines.length, slots.length]));

  if (!mcpId) {
    return (
      <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
        Select a cost plan
      </div>
    );
  }

  return (
    <div
      className="h-full flex flex-col overflow-hidden"
      onMouseUp={endDrag}
      onMouseLeave={() => dragging && endDrag()}
    >
      <div className="flex items-center gap-2 p-3 border-b border-border shrink-0">
        <span className={cn(text.muted, 'text-muted-foreground')}>
          Drag a range of cells, then type a quantity to fill all of them at once.
        </span>
        <span className={cn(text.muted, 'ml-auto text-muted-foreground')}>
          {lines.length} lines × {slots.length} slots
        </span>
      </div>

      {/* Toolbar is bulk-only — a single cell is just a plain input, click
          and type, blur commits. Dragging across more than one cell is
          the only case that needs a separate "apply to all" step. */}
      {sel && selCount > 1 && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-muted/40 shrink-0">
          <span className={text.body}>{selCount} cell{selCount > 1 ? 's' : ''} selected</span>
          <input
            type="number"
            min="0"
            step="1"
            value={fillValue}
            onChange={(e) => setFillValue(e.target.value)}
            placeholder="qty"
            autoFocus
            disabled={applying}
            className="w-20 text-right tabular-nums bg-background border border-border rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const v = parseFloat(fillValue);
                if (!Number.isNaN(v) && v >= 0) applyFill(v);
              }
              if (e.key === 'Escape') { setSel(null); setFillValue(''); }
            }}
          />
          <button
            type="button"
            data-testid={fillApplyTestId}
            disabled={applying || fillValue === ''}
            onClick={() => { const v = parseFloat(fillValue); if (!Number.isNaN(v) && v >= 0) applyFill(v); }}
            className="text-xs font-medium text-primary disabled:opacity-40"
          >
            {applying ? 'Applying…' : 'Fill'}
          </button>
          <button
            type="button" disabled={applying} onClick={() => applyFill(0)}
            className="text-xs text-muted-foreground hover:text-destructive transition-colors"
          >
            Clear
          </button>
          <button
            type="button" disabled={applying} onClick={() => { setSel(null); setFillValue(''); }}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Cancel
          </button>
        </div>
      )}

      <div className="flex-1 overflow-auto">
        <table className="text-xs border-collapse select-none">
          <thead className="sticky top-0 z-10">
            <tr>
              <th rowSpan={2} className="sticky left-0 z-20 bg-card border-b border-r border-border px-3 py-2 text-left align-bottom min-w-[220px]">
                Line
              </th>
              {areaGroups.map((g, i) => (
                <th key={`${g.name}-${i}`} colSpan={g.count} className="bg-card border-b border-r border-border px-1.5 py-1.5 text-center font-medium">
                  {g.name}
                </th>
              ))}
              <th rowSpan={2} className="bg-card border-b border-border px-3 py-2 text-right align-bottom min-w-[80px]">Allocated</th>
            </tr>
            <tr>
              {slots.map((s) => (
                <th key={s.sid} className="bg-card border-b border-r border-border px-1.5 py-1.5 text-center min-w-[52px] font-normal">
                  <div className="text-primary">{s.sub_area_label ?? s.name}</div>
                  {s.count != null && s.count > 1 && (
                    <div className="text-muted-foreground">×{s.count}</div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading && lines.length === 0 ? (
              <tr><td colSpan={slots.length + 2} className="px-3 py-6 text-center text-muted-foreground">…</td></tr>
            ) : lines.length === 0 ? (
              <tr><td colSpan={slots.length + 2} className="px-3 py-6 text-center text-muted-foreground">No lines on this plan yet</td></tr>
            ) : lines.map((line, r) => {
              const allocated = (line.room_placements ?? []).reduce((sum, p) => sum + p.qty, 0);
              const over = allocated > line.quantity;
              const isSelected = !!selectedId && selectedId === line.product_sid;
              return (
                <tr key={line.line_id ?? r}>
                  <td className="sticky left-0 z-10 bg-card border-b border-r border-border px-3 py-1.5">
                    <div
                      className={cn('flex items-center gap-2 min-w-0', line.product_sid && 'cursor-pointer')}
                      onClick={() => selectLine(line)}
                    >
                      {line.image_url ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img src={line.image_url} alt="" className="h-6 w-6 rounded object-cover flex-shrink-0" />
                      ) : (
                        <div className="h-6 w-6 rounded bg-muted flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <div className={cn('truncate', isSelected ? 'text-primary underline' : 'hover:text-primary hover:underline')}>
                          {line.product_name ?? '—'}
                        </div>
                        {line.source_line_label && (
                          <div className="truncate text-[10px] text-muted-foreground">
                            for: {line.source_line_label}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  {slots.map((slot, c) => {
                    const cellId = `${line.line_id}::${slot.sid}`;
                    const qty = placementMap.get(cellId) ?? null;
                    // A plausible room for this line's source schedule
                    // item, per the server-side resolver — shown only
                    // while the cell is still empty; once a qty is
                    // entered the value itself is the signal, so the
                    // suggestion tint would be redundant noise.
                    const isCandidate = !qty && (line.candidate_slot_sids ?? []).includes(slot.sid);
                    return (
                      <td
                        key={slot.sid}
                        onMouseDown={() => startSel(r, c)}
                        onMouseOver={() => extendSel(r, c)}
                        className={cn(
                          'border-b border-r border-border p-0 h-8',
                          isCandidate && 'bg-amber-500/10',
                          inSel(r, c) && selCount > 1 && 'bg-primary/15 ring-1 ring-primary/40 ring-inset',
                        )}
                      >
                        <input
                          key={`${cellId}-${qty}`}
                          data-testid={line.line_id ? cellTestId(cellId) : undefined}
                          type="number"
                          min="0"
                          step="1"
                          disabled={applying}
                          defaultValue={qty ?? ''}
                          placeholder="·"
                          onBlur={(e) => {
                            // A drag that ended on this cell still fires blur —
                            // the range-fill toolbar owns the commit once more
                            // than one cell is selected, so a lone cell's blur
                            // doesn't double-write on top of it.
                            if (selCount > 1) return;
                            const v = parseFloat(e.target.value);
                            if (line.line_id && !Number.isNaN(v) && v >= 0 && v !== (qty ?? 0)) {
                              editLine.mutate({ semantic_id: mcpId!, line_id: line.line_id, room_placement: { slot_sid: slot.sid, qty: v } });
                            }
                          }}
                          className={cn(
                            'w-full h-8 text-center tabular-nums bg-transparent border-0 focus:outline-none focus:ring-1 focus:ring-primary focus:ring-inset',
                            qty ? 'font-semibold' : 'text-muted-foreground/40',
                          )}
                        />
                      </td>
                    );
                  })}
                  <td className={cn('border-b border-border px-3 py-1.5 text-right tabular-nums font-medium', over && 'text-destructive')}>
                    {allocated} <span className="text-muted-foreground font-normal">/</span>{' '}
                    {/* The line's own real quantity, edited right where
                        the mismatch is actually seen — was read-only
                        text; fixing "125 / 1" used to mean leaving this
                        grid to find a qty control elsewhere. */}
                    <input
                      key={`qty-${line.line_id}-${line.quantity}`}
                      type="number"
                      min="0"
                      step="1"
                      disabled={applying || !line.line_id}
                      defaultValue={line.quantity}
                      onClick={(e) => e.stopPropagation()}
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        if (line.line_id && !Number.isNaN(v) && v >= 0 && v !== line.quantity) {
                          editLine.mutate({ semantic_id: mcpId!, line_id: line.line_id, quantity: v });
                        }
                      }}
                      className={cn(
                        'w-12 text-right tabular-nums bg-transparent border border-transparent hover:border-border focus:border-primary rounded px-1 focus:outline-none',
                        over ? 'text-destructive' : 'text-muted-foreground',
                      )}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
